import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import { logAccess } from '../../../lib/accessLogs';
import { resolveAnalysesToCatalogueIds } from '../../../lib/analyseMatcher';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import AnalyseChecklist, { CATEGORIES_ANALYSES } from '../../../components/feature/AnalyseChecklist';
import PrintableBonLaboratoire from '../../../components/feature/PrintableBonLaboratoire';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string | null;
  telephone: string | null;
  antecedents_medicaux: string | null;
  profession: string | null;
}

interface MedecinInfo {
  id: string;
  nom: string;
  prenom: string;
  specialite: string | null;
}

interface SoinRow {
  id: string;
  date: string;
  soin: string;
  tarif: string;
}

interface ConstantesVitales {
  taille: string;
  poids: string;
  tension_arterielle: string;
  temperature: string;
  libelle: string;
}

function calculerAge(dateNaissance: string | null): string {
  if (!dateNaissance) return '—';
  const naissance = new Date(dateNaissance);
  const aujourdhui = new Date();
  let age = aujourdhui.getFullYear() - naissance.getFullYear();
  const mois = aujourdhui.getMonth() - naissance.getMonth();
  if (mois < 0 || (mois === 0 && aujourdhui.getDate() < naissance.getDate())) {
    age--;
  }
  if (age < 2) {
    const moisBebe = Math.max(0, (aujourdhui.getFullYear() - naissance.getFullYear()) * 12 + aujourdhui.getMonth() - naissance.getMonth());
    return `${moisBebe} mois`;
  }
  return `${age} ans`;
}

export default function BonLaboratoirePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const rdvId = searchParams.get('rdvId');

  const [patient, setPatient] = useState<Patient | null>(null);
  const [medecinInfo, setMedecinInfo] = useState<MedecinInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bonCree, setBonCree] = useState(false);
  const [bonId, setBonId] = useState<string>('');
  const [isPrinting, setIsPrinting] = useState(false);
  const { mutate } = useOfflineMutation<any>();

  const [selectedAnalyses, setSelectedAnalyses] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [urgences, setUrgences] = useState(false);
  const [observationsCliniques, setObservationsCliniques] = useState('');
  const [dbCustomAnalyses, setDbCustomAnalyses] = useState<{ nom: string; items: { id: string; label: string }[] }[]>([]);
  const [catalogueEntries, setCatalogueEntries] = useState<{ id: string; code: string; nom: string }[]>([]);

  // Infos patient éditables
  const [patientTelephone, setPatientTelephone] = useState('');
  const [patientProfession, setPatientProfession] = useState('');
  const [patientAssure, setPatientAssure] = useState(false);
  const [medecinTraitant, setMedecinTraitant] = useState('');

  // Constantes vitales
  const [constantes, setConstantes] = useState<ConstantesVitales>({
    taille: '',
    poids: '',
    tension_arterielle: '',
    temperature: '',
    libelle: '',
  });

  // Tableau des soins
  const [soins, setSoins] = useState<SoinRow[]>([
    { id: crypto.randomUUID(), date: '', soin: '', tarif: '' }
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    loadData();
  }, [user, patientId, authLoading]);

  const loadData = async () => {
    try {
      setLoading(true);

      const { data: medecinData } = await supabase
        .from('medecins')
        .select('id, nom, prenom, specialite')
        .eq('user_id', user?.id)
        .single();

      if (medecinData) {
        setMedecinInfo(medecinData);
        setMedecinTraitant(`Dr. ${medecinData.prenom} ${medecinData.nom}`);
      }

      if (patientId) {
        const { data: patientData } = await supabase
          .from('patients')
          .select('id, nom, prenom, date_naissance, telephone, antecedents_medicaux')
          .eq('id', patientId)
          .single();

        if (patientData) {
          setPatient(patientData);
          if (patientData.telephone) setPatientTelephone(patientData.telephone);
        }
      }

      // Charger les analyses personnalisées depuis la base
      loadCustomAnalysesFromDb();

      // Charger le catalogue complet pour le matching des analyses
      loadCatalogueForMatching();
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomAnalysesFromDb = async () => {
    try {
      const { data } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('nom')
        .eq('categorie', 'Analyses personnalisées')
        .order('nom');
      if (data && data.length > 0) {
        const items = data.map((a: any) => ({
          id: a.nom,
          label: a.nom,
        }));
        setDbCustomAnalyses([{ nom: 'Analyses personnalisées', items }]);
      }
    } catch (err) {
      console.error('Erreur chargement analyses personnalisées:', err);
    }
  };

  const loadCatalogueForMatching = async () => {
    try {
      const { data } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('id, code, nom');
      if (data) {
        setCatalogueEntries(data);
      }
    } catch (err) {
      console.error('Erreur chargement catalogue:', err);
    }
  };

  const toggleAnalyse = (id: string) => {
    setSelectedAnalyses(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleConstanteChange = (field: keyof ConstantesVitales, value: string) => {
    setConstantes(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSoinRow = () => {
    setSoins(prev => [...prev, { id: crypto.randomUUID(), date: '', soin: '', tarif: '' }]);
  };

  const handleRemoveSoinRow = (id: string) => {
    if (soins.length > 1) {
      setSoins(prev => prev.filter(s => s.id !== id));
    }
  };

  const handleSoinChange = (id: string, field: keyof SoinRow, value: string) => {
    setSoins(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handlePrintImageReady = () => {
    setTimeout(() => {
      window.print();
      setTimeout(() => setIsPrinting(false), 500);
    }, 300);
  };

  const handlePrint = () => {
    if (selectedAnalyses.length === 0) {
      alert('Veuillez sélectionner au moins une analyse avant d\'imprimer');
      return;
    }
    const img = new Image();
    img.src = 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/letterhead.webp';
    img.onload = () => setIsPrinting(true);
    img.onerror = () => setIsPrinting(true);
  };

  const getAnalysesLabels = () => {
    const allItems = CATEGORIES_ANALYSES.flatMap(c => c.items);
    return selectedAnalyses.map(id => {
      const item = allItems.find(i => i.id === id);
      return item ? item.label : id;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedAnalyses.length === 0) {
      alert('Veuillez sélectionner au moins une analyse');
      return;
    }

    if (!patientId || !medecinInfo) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const analysesLabels = getAnalysesLabels();
    const typeAnalyseStr = analysesLabels.join(' ; ');

    // Résoudre les analyses : match exact ou création automatique dans le catalogue
    let matchedIds: string[] = [];
    try {
      matchedIds = (await resolveAnalysesToCatalogueIds(analysesLabels, catalogueEntries)).ids;
    } catch (err) {
      console.error('Erreur résolution catalogue:', err);
    }

    try {
      setSaving(true);
      const online = navigator.onLine;

      // Création du bon via offline-first
      const bonResult = await mutate({
        table: 'bons_laboratoire',
        operation: 'INSERT',
        data: {
          patient_id: patientId,
          medecin_id: medecinInfo.id,
          type_analyse: typeAnalyseStr,
          description: description.trim() || null,
          urgences,
          observations_cliniques: observationsCliniques.trim() || null,
          statut: 'en_attente',
          date_prescription: new Date().toISOString().split('T')[0],
          taille: constantes.taille.trim() || null,
          poids: constantes.poids.trim() || null,
          tension_arterielle: constantes.tension_arterielle.trim() || null,
          temperature: constantes.temperature.trim() || null,
          libelle_constantes: constantes.libelle.trim() || null,
          patient_profession: patientProfession.trim() || null,
          patient_assure: patientAssure,
          patient_telephone: patientTelephone.trim() || null,
          medecin_traitant: medecinTraitant.trim() || null,
          analyses_ids: matchedIds.length > 0 ? matchedIds : null,
        },
        metadata: {
          summary: `Bon labo — ${patient.prenom} ${patient.nom} (${selectedAnalyses.length} analyse(s))`,
          performedBy: 'medecin',
        },
        onlineExecutor: async () => {
          const { data: bonData, error } = await supabase
            .from('bons_laboratoire')
            .insert({
              patient_id: patientId,
              medecin_id: medecinInfo.id,
              type_analyse: typeAnalyseStr,
              description: description.trim() || null,
              urgences,
              observations_cliniques: observationsCliniques.trim() || null,
              statut: 'en_attente',
              date_prescription: new Date().toISOString().split('T')[0],
              taille: constantes.taille.trim() || null,
              poids: constantes.poids.trim() || null,
              tension_arterielle: constantes.tension_arterielle.trim() || null,
              temperature: constantes.temperature.trim() || null,
              libelle_constantes: constantes.libelle.trim() || null,
              patient_profession: patientProfession.trim() || null,
              patient_assure: patientAssure,
              patient_telephone: patientTelephone.trim() || null,
              medecin_traitant: medecinTraitant.trim() || null,
              analyses_ids: matchedIds.length > 0 ? matchedIds : null,
            })
            .select()
            .single();

          if (error) throw error;
          return bonData;
        },
      });

      // Secondary operations (online only)
      if (online && user && bonResult?.id) {
        const bonIdResult = bonResult.id;
        setBonId(bonIdResult);

        // Mettre à jour le rendez-vous si on vient d'une consultation
        if (rdvId) {
          try {
            await supabase
              .from('rendez_vous')
              .update({ statut: 'en_attente_resultats' })
              .eq('id', rdvId);
          } catch (rdvError) {
            console.error('Erreur mise à jour RDV:', rdvError);
          }
        }

        // Log access
        await logAccess({
          userId: user.id,
          userRole: 'medecin',
          actionType: 'CREATE',
          resourceType: 'BON_LABORATOIRE',
          resourceId: bonIdResult,
          patientId: patientId,
          details: {
            patient_nom: patient ? `${patient.prenom} ${patient.nom}` : 'Inconnu',
            type_analyse: typeAnalyseStr,
            urgences,
            action: 'Création d\'un bon de laboratoire'
          }
        });

        // Notifier les laborantins
        try {
          const { data: laborantins } = await supabase
            .from('laborantins')
            .select('email, nom, prenom, user_id')
            .eq('bloque', false);

          if (laborantins && laborantins.length > 0) {
            const patientName = patient ? `${patient.prenom} ${patient.nom}` : 'Patient';
            const urgenceLabel = urgences ? 'URGENT - ' : '';

            for (const labo of laborantins) {
              if (labo.email) {
                await supabase.functions.invoke('send-email', {
                  body: {
                    to: labo.email,
                    toName: `${labo.prenom} ${labo.nom}`,
                    subject: `${urgenceLabel}Nouvelles analyses - ${patientName}`,
                    htmlContent: '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:40px;font-family:Arial,sans-serif;background:#f5f5f5;"><div style="max-width:600px;margin:0 auto;background:white;padding:40px;border-radius:10px;"><h1 style="color:' + (urgences ? '#ef4444' : '#10B981') + ';margin-bottom:20px;">' + (urgences ? 'Analyses urgentes' : 'Nouvelles analyses') + '</h1><p>Bonjour <strong>' + labo.prenom + '</strong>,</p><p>Un nouveau bon de laboratoire a été créé :</p><div style="background:#f9fafb;border-left:4px solid ' + (urgences ? '#ef4444' : '#10B981') + ';padding:20px;margin:20px 0;border-radius:5px;"><p style="margin:5px 0;"><strong>Patient :</strong> ' + patientName + '</p><p style="margin:5px 0;"><strong>Nombre d\'analyses :</strong> ' + selectedAnalyses.length + '</p><p style="margin:5px 0;"><strong>Analyses demandées :</strong></p><ul style="margin:10px 0;padding-left:20px;">' + analysesLabels.map(l => '<li>' + l + '</li>').join('') + '</ul>' + (description ? '<p style="margin:5px 0;"><strong>Description :</strong> ' + description + '</p>' : '') + (observationsCliniques ? '<p style="margin:5px 0;"><strong>Observations :</strong> ' + observationsCliniques + '</p>' : '') + '</div>' + (urgences ? '<div style="background:#fef2f2;border:2px solid #ef4444;padding:15px;margin:20px 0;border-radius:8px;text-align:center;"><p style="color:#dc2626;font-weight:bold;margin:0;">ANALYSE URGENTE - À traiter en priorité</p></div>' : '') + '<p style="color:#666;font-size:14px;">Consultez votre tableau de bord pour prendre en charge cette analyse.</p><hr style="margin:30px 0;border:none;border-top:1px solid #eee;"><p style="color:#999;font-size:12px;text-align:center;">Clinique Sainte-Famille - Service Laboratoire</p></div></body></html>'
                  }
                });
              }
            }
          }
        } catch (notifError) {
          console.error('Erreur envoi notifications laborantins:', notifError);
        }
      } else if (bonResult?.id && !bonResult.id.startsWith('pending-')) {
        setBonId(bonResult.id);
      }

      if (!online) {
        alert('Bon de laboratoire enregistré en mode hors-ligne. Synchronisation automatique au retour de la connexion.');
      }

      setBonCree(true);
    } catch (error) {
      console.error('Erreur création bon de laboratoire:', error);
      alert('Erreur lors de la création du bon de laboratoire');
    } finally {
      setSaving(false);
    }
  };

  const handleContinuerPrescription = () => {
    navigate(`/medecin/creer-prescription?patientId=${patientId}`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold text-gray-900">Patient non trouvé</p>
          <button
            onClick={() => navigate('/medecin/rendez-vous')}
            className="mt-6 px-6 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark whitespace-nowrap cursor-pointer"
          >
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  const practitionerFullName = medecinInfo
    ? `Dr. ${medecinInfo.prenom} ${medecinInfo.nom}`
    : 'Médecin';

  const todayStr = new Date().toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const agePatient = calculerAge(patient.date_naissance);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="no-print">
        <Header />
      </div>

      <div className="bg-white shadow-sm border-b border-gray-200 no-print">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Bon de Laboratoire</h1>
              <p className="mt-2 text-gray-600">
                Patient : {patient.prenom} {patient.nom}
              </p>
            </div>
            <button
              onClick={() => navigate('/medecin/rendez-vous')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Retour
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 no-print">
        {bonCree ? (
          /* --- SUCCÈS : Bon créé --- */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-clinic-blue/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-clinic-blue"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bon de laboratoire créé avec succès !</h2>
              <p className="text-gray-600">
                Le bon a été transmis au laboratoire{urgences ? ' en priorité' : ''}.
              </p>
              {selectedAnalyses.length > 0 && (
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {selectedAnalyses.map(id => {
                    const allItems = CATEGORIES_ANALYSES.flatMap(c => c.items);
                    const item = allItems.find(i => i.id === id);
                    return (
                      <span key={id} className="px-3 py-1 bg-clinic-blue/10 text-clinic-blue-dark text-sm rounded-full border border-clinic-blue/30">
                        {item ? item.label : id}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handlePrint}
                className="w-full sm:w-auto px-8 py-3 bg-white text-clinic-blue border-2 border-clinic-blue rounded-lg hover:bg-clinic-blue/10 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base font-medium"
              >
                <i className="ri-printer-line"></i>
                Imprimer le bon
              </button>
              <button
                onClick={handleContinuerPrescription}
                className="w-full sm:w-auto px-8 py-3 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base font-medium"
              >
                <i className="ri-file-list-3-line"></i>
                Créer une prescription
              </button>
              <button
                onClick={() => navigate('/medecin/rendez-vous')}
                className="w-full sm:w-auto px-8 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-arrow-left-line"></i>
                Retour aux rendez-vous
              </button>
            </div>
          </div>
        ) : (
          /* --- FORMULAIRE --- */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* ===== CONSTANTES VITALES ===== */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-heart-pulse-line text-clinic-blue"></i>
                Constantes vitales du jour
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Taille (cm)</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Poids au jour (kg)</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">T.A. (mmHg)</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Température (°C)</th>
                      <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Libellé</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={constantes.taille}
                          onChange={(e) => handleConstanteChange('taille', e.target.value)}
                          placeholder="ex: 170"
                          className="w-full px-2.5 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={constantes.poids}
                          onChange={(e) => handleConstanteChange('poids', e.target.value)}
                          placeholder="ex: 68"
                          className="w-full px-2.5 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={constantes.tension_arterielle}
                          onChange={(e) => handleConstanteChange('tension_arterielle', e.target.value)}
                          placeholder="ex: 12/8"
                          className="w-full px-2.5 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={constantes.temperature}
                          onChange={(e) => handleConstanteChange('temperature', e.target.value)}
                          placeholder="ex: 37.2"
                          className="w-full px-2.5 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={constantes.libelle}
                          onChange={(e) => handleConstanteChange('libelle', e.target.value)}
                          placeholder="Observations..."
                          className="w-full px-2.5 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ===== INFOS PATIENT ===== */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-clinic-blue"></i>
                Informations du patient
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
                    {patient.nom}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
                    {patient.prenom}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Âge</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
                    {agePatient}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Téléphone</label>
                  <input
                    type="tel"
                    value={patientTelephone}
                    onChange={(e) => setPatientTelephone(e.target.value)}
                    placeholder="+229 XX XX XX XX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
                  <input
                    type="text"
                    value={patientProfession}
                    onChange={(e) => setPatientProfession(e.target.value)}
                    placeholder="Profession du patient"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assuré</label>
                  <div className="flex items-center gap-4 mt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="assure"
                        checked={patientAssure}
                        onChange={() => setPatientAssure(true)}
                        className="w-4 h-4 text-clinic-blue focus:ring-clinic-blue cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Oui</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="assure"
                        checked={!patientAssure}
                        onChange={() => setPatientAssure(false)}
                        className="w-4 h-4 text-clinic-blue focus:ring-clinic-blue cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">Non</span>
                    </label>
                  </div>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Médecin traitant</label>
                  <input
                    type="text"
                    value={medecinTraitant}
                    onChange={(e) => setMedecinTraitant(e.target.value)}
                    placeholder="Nom du médecin traitant"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* ===== ANTÉCÉDENTS MÉDICAUX ===== */}
            {patient.antecedents_medicaux && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-file-history-line text-clinic-blue"></i>
                  Antécédents médicaux
                </h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                  {patient.antecedents_medicaux}
                </div>
              </div>
            )}

            {/* Checklist des analyses */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Analyses demandées <span className="text-red-500">*</span>
              </label>
              <AnalyseChecklist
                selectedIds={selectedAnalyses}
                onToggle={toggleAnalyse}
                accentColor="blue"
                extraCategories={dbCustomAnalyses}
              />
            </div>

            {/* Description */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description détaillée <span className="text-gray-500">(facultatif)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Précisions sur les analyses demandées, paramètres spécifiques..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none text-sm"
                maxLength={500}
              />
            </div>

            {/* Observations cliniques */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observations cliniques <span className="text-gray-500">(facultatif)</span>
              </label>
              <textarea
                value={observationsCliniques}
                onChange={(e) => setObservationsCliniques(e.target.value)}
                rows={4}
                placeholder="Signes cliniques, éléments pouvant influencer les résultats..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none text-sm"
                maxLength={500}
              />
            </div>

            {/* Urgence */}
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <input
                type="checkbox"
                id="urgences"
                checked={urgences}
                onChange={(e) => setUrgences(e.target.checked)}
                className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500 cursor-pointer"
              />
              <label htmlFor="urgences" className="flex items-center gap-2 text-sm font-medium text-amber-800 cursor-pointer">
                <i className="ri-alert-line text-lg"></i>
                Analyse urgente (à traiter en priorité par le laboratoire)
              </label>
            </div>

            {/* Tableau des soins */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Soins associés <span className="text-gray-500">(facultatif)</span>
                </label>
                <button
                  type="button"
                  onClick={handleAddSoinRow}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-clinic-blue hover:bg-clinic-blue/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Ajouter un soin
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-[170px]">Date</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Soin</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-[120px]">Tarif (FCFA)</th>
                      <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-[50px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {soins.map((soin) => (
                      <tr key={soin.id} className="border-b border-gray-100">
                        <td className="px-3 py-2">
                          <input
                            type="date"
                            value={soin.date}
                            onChange={(e) => handleSoinChange(soin.id, 'date', e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={soin.soin}
                            onChange={(e) => handleSoinChange(soin.id, 'soin', e.target.value)}
                            placeholder="Nom du soin..."
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={soin.tarif}
                            onChange={(e) => handleSoinChange(soin.id, 'tarif', e.target.value)}
                            placeholder="0"
                            min="0"
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {soins.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSoinRow(soin.id)}
                              className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/medecin/rendez-vous')}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handlePrint}
                disabled={selectedAnalyses.length === 0}
                className="px-6 py-2 bg-white text-clinic-blue border-2 border-clinic-blue rounded-lg hover:bg-clinic-blue/10 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-printer-line"></i>
                Imprimer
              </button>
              <button
                type="button"
                onClick={handleContinuerPrescription}
                className="px-6 py-2 bg-white text-clinic-blue border-2 border-clinic-blue rounded-lg hover:bg-clinic-blue/10 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-file-list-3-line"></i>
                Prescription
              </button>
              <button
                type="submit"
                disabled={saving || selectedAnalyses.length === 0}
                className="px-6 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer"
              >
                {saving ? 'Création en cours...' : `Créer le bon (${selectedAnalyses.length} analyse${selectedAnalyses.length > 1 ? 's' : ''})`}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Print container */}
      {isPrinting && (
        <div className="print-container">
          <PrintableBonLaboratoire
            patientName={`${patient.prenom} ${patient.nom}`}
            practitionerName={practitionerFullName}
            practitionerTitle={medecinInfo?.specialite || undefined}
            date={todayStr}
            analyses={getAnalysesLabels()}
            description={description || undefined}
            observations={observationsCliniques || undefined}
            urgences={urgences}
            soins={soins}
            constantes={constantes}
            patientAge={agePatient}
            patientTelephone={patientTelephone}
            patientProfession={patientProfession}
            patientAssure={patientAssure}
            medecinTraitant={medecinTraitant}
            antecedentsMedicaux={patient.antecedents_medicaux || undefined}
            verificationUrl={bonId ? `${window.location.origin}/verifier-document?type=bon_laboratoire&id=${bonId}` : undefined}
            onReady={handlePrintImageReady}
          />
        </div>
      )}

      <div className="no-print">
        <Footer />
      </div>
    </div>
  );
}